import { zodResolver } from '@hookform/resolvers/zod'
import { FirebaseError } from 'firebase/app'
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useT } from '@/shared/i18n'
import type { I18n } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { getFirebaseAuth } from './firebase'

/**
 * EL LÍMITE DE ESTA PANTALLA, ESCRITO ACÁ PARA QUE NADIE LO DESCUBRA SOLO.
 *
 * El login se dibuja **antes de que exista una sesión**, así que todavía no hay
 * `users.locale` que consultar: no sabemos en qué idioma lee quien está
 * mirando. Con el `I18nProvider` montado sin `locale`, todo lo de abajo sale en
 * el idioma por defecto del sistema (`es`).
 *
 * Es exactamente el mismo motivo por el que el backend no traduce los errores
 * de `app/core/security.py` (`NoAutenticadoError`, `UsuarioNoInvitadoError`):
 * se levantan antes de resolver la identidad. Ver `docs/i18n.md`, "Lo que no se
 * puede traducir y por qué".
 *
 * **Aun así todos los textos de esta pantalla viven en el catálogo**, y a
 * propósito: el día que se decida leer una preferencia previa —una cookie del
 * último login, un `locale` que llegue del backend en `/config.js`— alcanza con
 * pasarle ese valor al `I18nProvider`. No hay que volver a tocar este archivo.
 *
 * **Lo que NO hacemos es adivinar.** Nada de `navigator.language` ni de un
 * `?lang=` en la URL: la decisión tomada es que *el idioma es de la persona y
 * sale de su perfil* (`users.locale`). Detectarlo por navegador daría un idioma
 * distinto del que esa persona eligió apenas entre, que es peor que mostrar
 * siempre el mismo.
 */

function makeSchema(t: I18n['t']) {
  return z.object({
    email: z.string().email(t('auth.validation.email')),
    password: z.string().min(6, t('auth.validation.password')),
  })
}

type LoginValues = z.infer<ReturnType<typeof makeSchema>>

/**
 * Mensajes de Firebase, sin filtrar si el usuario existe o no.
 *
 * Lo que se muestra es copia NUESTRA elegida por `error.code`, no el `message`
 * crudo de Firebase (que viene en inglés técnico y además distingue "usuario no
 * encontrado" de "contraseña incorrecta", que es justo lo que no queremos
 * decir). Por eso sí va al catálogo: el texto lo escribimos nosotros.
 */
function loginErrorMessage(error: unknown, t: I18n['t']): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return t('auth.error.invalidCredentials')
      case 'auth/too-many-requests':
        return t('auth.error.tooManyRequests')
      case 'auth/user-disabled':
        return t('auth.error.userDisabled')
      case 'auth/network-request-failed':
        return t('auth.error.network')
      case 'auth/unauthorized-domain':
        return t('auth.error.unauthorizedDomain')
      default:
        return t('auth.error.generic')
    }
  }
  return t('auth.error.generic')
}

const inputClasses = cn(
  'hairline h-8 w-full rounded-[var(--radius-control)] bg-surface-1 px-2 text-sm text-text-1',
  'placeholder:text-text-3',
)

export function LoginPage() {
  const t = useT()
  const [formError, setFormError] = useState<string | null>(null)

  const schema = useMemo(() => makeSchema(t), [t])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null)
    const auth = getFirebaseAuth()

    if (!auth) {
      setFormError(t('auth.error.notConfigured'))
      return
    }

    try {
      await signInWithEmailAndPassword(auth, email, password)
      // El redirect lo hace <RedirectIfAuthenticated> cuando cambia el id token.
    } catch (error) {
      setFormError(loginErrorMessage(error, t))
    }
  })

  /**
   * Google primero: el equipo entra con su cuenta de Workspace y no maneja una
   * contraseña más. El formulario de abajo queda para quien no tiene Google
   * (los contactos de cliente, típicamente).
   *
   * El backend no cambia: recibe un ID token de Firebase igual que con email y
   * contraseña, y la identidad se sigue resolviendo contra `users`. Un Google
   * válido sin fila previa NO entra — no hay auto-provisioning.
   */
  const [conGoogle, setConGoogle] = useState(false)
  const entrarConGoogle = async () => {
    setFormError(null)
    const auth = getFirebaseAuth()
    if (!auth) {
      setFormError(t('auth.error.notConfigured'))
      return
    }
    setConGoogle(true)
    try {
      await signInWithPopup(auth, new GoogleAuthProvider())
    } catch (error) {
      // Cerrar el popup no es un error que haya que mostrar.
      const code = error instanceof FirebaseError ? error.code : ''
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setFormError(loginErrorMessage(error, t))
      }
    } finally {
      setConGoogle(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
      <h1 className="text-lg">{t('auth.title')}</h1>

      <button
        type="button"
        onClick={entrarConGoogle}
        disabled={conGoogle || isSubmitting}
        className={cn(
          'h-8 rounded-[var(--radius-control)] bg-signal text-sm font-medium text-on-signal',
          'transition-opacity duration-[var(--motion-fast)] disabled:opacity-60',
        )}
      >
        {conGoogle ? t('auth.signingIn') : t('auth.signInWithGoogle')}
      </button>

      <p className="text-2xs text-text-3">{t('auth.or')}</p>

      <label className="flex flex-col gap-1">
        <span className="text-xs text-text-2">{t('common.label.email')}</span>
        <input
          type="email"
          autoComplete="email"
          autoFocus
          className={inputClasses}
          aria-invalid={Boolean(errors.email)}
          {...register('email')}
        />
        {errors.email ? (
          <span className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
            {errors.email.message}
          </span>
        ) : null}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs text-text-2">{t('auth.password')}</span>
        <input
          type="password"
          autoComplete="current-password"
          className={inputClasses}
          aria-invalid={Boolean(errors.password)}
          {...register('password')}
        />
        {errors.password ? (
          <span className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
            {errors.password.message}
          </span>
        ) : null}
      </label>

      {formError ? (
        <p role="alert" className="text-xs" style={{ color: 'hsl(var(--overdue))' }}>
          {formError}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={isSubmitting || conGoogle}
        className={cn(
          'hairline h-8 rounded-[var(--radius-control)] bg-surface-1 text-sm font-medium text-text-1',
          'transition-opacity duration-[var(--motion-fast)] disabled:opacity-60',
        )}
      >
        {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
      </button>

      <p className="text-2xs text-text-3">{t('auth.clientHint')}</p>
    </form>
  )
}
